import { useCallback, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';

import { getSessionTimeoutConfig } from 'config/sessionTimeout';
import { useSelector } from 'react-redux';

import SessionTimeoutModal from 'components/SessionTimeout/SessionTimeoutModal';

import { useIdleTimer } from 'hooks/useIdleTimer';

import { terminateSession } from 'services/auth';

import type { RootState } from 'store/store';

type SessionTimeoutProviderProps = {
  children: ReactNode;
};

const hasToken = (value: string | null | undefined): boolean => Boolean(value?.trim());

const SessionTimeoutProvider = ({ children }: SessionTimeoutProviderProps) => {
  const [isTerminating, setIsTerminating] = useState(false);
  const isTerminatingRef = useRef(false);

  const isSessionAuthenticated = useSelector((state: RootState) => {
    const adminHasToken =
      hasToken(state.adminSession.idToken) || hasToken(state.adminSession.accessToken);
    const userHasToken =
      hasToken(state.userSession.idToken) || hasToken(state.userSession.accessToken);
    return adminHasToken || userHasToken;
  });

  const { idleTimeoutMs, warningDurationMs } = useMemo(() => getSessionTimeoutConfig(), []);

  const endSession = useCallback(async () => {
    if (isTerminatingRef.current) {
      return;
    }

    isTerminatingRef.current = true;
    setIsTerminating(true);
    try {
      await terminateSession();
    } finally {
      isTerminatingRef.current = false;
      setIsTerminating(false);
    }
  }, []);

  const handleTimeout = useCallback(() => {
    void endSession();
  }, [endSession]);

  const { isWarningOpen, warningSecondsRemaining, continueSession } = useIdleTimer({
    enabled: isSessionAuthenticated && !isTerminating,
    idleTimeoutMs,
    warningDurationMs,
    onTimeout: handleTimeout,
  });

  return (
    <>
      {children}
      <SessionTimeoutModal
        open={isWarningOpen}
        secondsRemaining={warningSecondsRemaining}
        onContinueSession={continueSession}
        onLogout={handleTimeout}
        isTerminating={isTerminating}
      />
    </>
  );
};

export default SessionTimeoutProvider;
